/**
 * Property CSV Processor
 * Simplified processor specifically for property CSV uploads
 */

import type {
  CSVRow,
  PropertyRecord,
  PropertyTransformer,
  PropertyUploadContext,
} from '@/types/csv-upload'
import {
  PMS_PROVIDERS,
  Property,
  validatePMSProvider,
  type PMSProvider,
} from '@super/shared-types'
import type { PropertyDuplicateDetectionResult } from './property-duplicate-detector'
import { appfolioPropertyTransformer } from './transformers/appfolio-property-transformer'
import {
  BaseCSVProcessor,
  type BaseCSVProcessingOptions,
  type BaseCSVResult,
} from './base-csv-processor'

export interface PropertyCSVProcessingResult extends BaseCSVResult {
  properties: PropertyRecord[]
  sampleRows?: CSVRow[]
  headers?: string[]
  duplicateDetection?: PropertyDuplicateDetectionResult
}

export interface PropertyCSVPreviewResult extends PropertyCSVProcessingResult {
  sampleRows: CSVRow[]
  headers: string[]
}

/**
 * Get the appropriate property transformer for a PMS provider
 */
function getPropertyTransformer(provider: PMSProvider): PropertyTransformer {
  switch (provider) {
    case PMS_PROVIDERS.APPFOLIO:
      return appfolioPropertyTransformer
    // TODO: Add other property transformers when implemented
    // case PMS_PROVIDERS.BUILDIUM:
    //   return buildiumPropertyTransformer
    // case PMS_PROVIDERS.RENTVINE:
    //   return rentvinePropertyTransformer
  }

  // This should never happen with proper typing, but TypeScript needs it
  throw new Error(`No transformer implemented for PMS provider: ${provider}`)
}

/**
 * Property CSV processor extending base functionality
 */
export class PropertyCSVProcessor extends BaseCSVProcessor<
  PropertyRecord,
  PropertyCSVProcessingResult,
  PropertyUploadContext
> {
  private transformer: PropertyTransformer

  constructor(
    pmsProvider: string,
    companyId: string,
    options: BaseCSVProcessingOptions = {},
  ) {
    // Validate inputs
    if (!companyId?.trim()) {
      throw new Error('Company ID is required')
    }

    // Validate and normalize PMS provider
    const validatedProvider = validatePMSProvider(pmsProvider)

    // Create property upload context
    const context: PropertyUploadContext = {
      companyId,
      pmsProvider: validatedProvider,
    }

    super(context, options)
    this.transformer = getPropertyTransformer(validatedProvider)
  }

  /**
   * Transform a CSV row into a PropertyRecord
   */
  protected transformRow(
    row: CSVRow,
    _rowNumber: number,
  ): PropertyRecord | null {
    return this.transformer.transformProperty(row, this.context)
  }

  /**
   * Validate a PropertyRecord
   */
  protected validateRecord(
    record: PropertyRecord,
    _rowNumber: number,
  ): {
    isValid: boolean
    errors: string[]
    warnings: string[]
  } {
    return this.transformer.validateProperty(record, this.context)
  }

  /**
   * Create the result object with properties
   */
  protected createResult(data: {
    success: boolean
    totalRows: number
    processedRows: number
    skipped: number
    records: PropertyRecord[]
    errors: BaseCSVResult['errors']
    warnings: BaseCSVResult['warnings']
    sampleRows: CSVRow[]
    headers: string[]
    memoryUsage: {
      used: number
      limit: number
      percentage: number
    }
  }): PropertyCSVProcessingResult {
    return {
      success: data.success,
      totalRows: data.totalRows,
      processedRows: data.processedRows,
      skipped: data.skipped,
      properties: data.records,
      errors: data.errors,
      warnings: data.warnings,
      sampleRows: data.sampleRows,
      headers: data.headers,
      memoryUsage: data.memoryUsage,
    }
  }

  /**
   * Create an error result
   */
  protected createErrorResult(
    errors: BaseCSVResult['errors'],
    memoryStatus: { used: number; limit: number; percentage: number },
  ): PropertyCSVProcessingResult {
    return {
      success: false,
      totalRows: 0,
      processedRows: 0,
      skipped: 0,
      properties: [],
      errors,
      warnings: [],
      sampleRows: [],
      headers: [],
      memoryUsage: memoryStatus,
    }
  }

  /**
   * Detect duplicates in the processed properties
   */
  async detectDuplicates(
    properties: PropertyRecord[],
    existingProperties: Property[],
  ): Promise<PropertyDuplicateDetectionResult> {
    const { detectPropertyDuplicates } = await import(
      './property-duplicate-detector'
    )
    return detectPropertyDuplicates(properties, existingProperties)
  }

  /**
   * Full processing pipeline: parse, transform, and detect duplicates
   */
  async processFile(
    file: File,
    existingProperties: Property[] = [],
  ): Promise<PropertyCSVProcessingResult> {
    // Step 1: Parse and transform
    const preview = await this.parseCSV(file)

    if (!preview.success) {
      return preview
    }

    // Step 2: Detect duplicates if we have valid properties
    if (preview.properties.length > 0) {
      try {
        const duplicateDetection = await this.detectDuplicates(
          preview.properties,
          existingProperties,
        )

        return {
          ...preview,
          duplicateDetection,
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error occurred during duplicate detection'

        preview.errors.push({
          row: 0,
          message: `Duplicate detection failed: ${errorMessage}`,
          data: {
            phase: 'duplicate_detection',
            propertiesCount: preview.properties.length,
          },
        })
        return {
          ...preview,
          success: false,
        }
      }
    }

    return preview
  }

  /**
   * Get statistics about the processed data
   */
  getStatistics(result: PropertyCSVProcessingResult): {
    totalRows: number
    validProperties: number
    errors: number
    warnings: number
    memoryUsageMB: number
  } {
    return {
      totalRows: result.totalRows,
      validProperties: result.properties.length,
      errors: result.errors.length,
      warnings: result.warnings.length,
      memoryUsageMB: this.getUsageMB(),
    }
  }
}
